// src/pages/DetailPlaylistPage.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./DetailPlaylistPage.scss";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "../api/axios"; // твой axios instance с baseURL API

const VIDEO_EXT_RE = /\.(mp4|webm|mov|mkv|avi)$/i;
const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i;
const EMPTY_HIDDEN_FILE_IDS = new Set();
const HIDDEN_FILE_IDS_BY_PLAYLIST = {
    "14": new Set([
        191,  // Максим желтый
        471,  // Декед ноябрь
        477,  // декед ноябрь v2
        480,  // Атлас Балаково
        483,  // Атлас Нижний Новгород
        484,  // Атлас Оренбург
        1151, // Блокпост Воронеж
        1152, // Блокпост Краснодар
        1485, // Рождество промо
        1524, // HAVAL
        1933, // Василий Негрей
    ]),
};

function inferMediaKind(item) {
    const mime = String(item?.type || "").toLowerCase();
    const url = String(item?.url || "");

    if (mime.startsWith("video/") || VIDEO_EXT_RE.test(url)) {
        return "video";
    }

    if (mime.startsWith("image/") || IMAGE_EXT_RE.test(url)) {
        return "image";
    }

    return "file";
}

function getMediaKindLabel(kind) {
    if (kind === "video") {
        return "Видео";
    }

    if (kind === "image") {
        return "Изображение";
    }

    return "Файл";
}

function getPlaylistCategoryLabel(type) {
    if (type === "main") {
        return "Основной";
    }

    if (type === "promo") {
        return "Промо";
    }

    if (type === "ad") {
        return "Реклама";
    }

    return null;
}

export default function DetailPlaylistPage() {
    const { playlistId } = useParams();

    // плейлист
    const [titlePlaylist, setTitlePlaylist] = useState("");
    const [currentPlaylist, setCurrentPlaylist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingOrder, setSavingOrder] = useState(false);

    // галерея
    const [mediaList, setMediaList] = useState([]);

    // компании (и для назначения плейлиста, и для таргетинга)
    const [companies, setCompanies] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);

    // форма добавления нового файла
    const [addFormOpen, setAddFormOpen] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [adding, setAdding] = useState(false);

    // режим вставки нового файла
    // 'once' | 'interleave' | 'afterType' | 'beforeType'
    const [insertMode, setInsertMode] = useState("once");

    // once
    const [oncePosition, setOncePosition] = useState("");

    // interleave
    const [intervalN, setIntervalN] = useState(1);

    // общее
    const [playTime, setPlayTime] = useState("");

    // якорный тип файла для afterType/beforeType
    const [targetType, setTargetType] = useState("main"); // "main" | "promo" | "ad"

    // === ТАРГЕТИНГ ДЛЯ КОНКРЕТНОГО ЭЛЕМЕНТА ПЛЕЙЛИСТА ===
    // какой элемент плейлиста сейчас редактируем (id строки FilePlayList)
    const [ruleEditItemId, setRuleEditItemId] = useState(null);

    // какой FileGlobal лежит внутри этой строки (для массового применения)
    const [ruleEditFileGlobalId, setRuleEditFileGlobalId] = useState(null);

    // локальная форма: режим ('whitelist' | 'blacklist' | null)
    const [ruleMode, setRuleMode] = useState(null);

    // локальная форма: какие компании выбраны
    const [ruleSelectedCompanyIds, setRuleSelectedCompanyIds] = useState([]);

    // фильтр поиска компаний внутри модалки таргетинга
    const [ruleSearch, setRuleSearch] = useState("");

    // применять ли таргетинг ко всем вхождениям этого же файла
    const [ruleApplyAllSame, setRuleApplyAllSame] = useState(false);

    const hiddenFileIds =
        HIDDEN_FILE_IDS_BY_PLAYLIST[playlistId] || EMPTY_HIDDEN_FILE_IDS;
    const visiblePlaylist = currentPlaylist.filter(
        (item) => !hiddenFileIds.has(Number(item.fileGlobalId))
    );

    useEffect(() => {
        getPlayListById();
        getGallery();
        fetchCompanies();
        // eslint-disable-next-line
    }, [playlistId]);

    function getPlayListById() {
        setLoading(true);
        api.get(`/admin/playlists/${playlistId}/files`)
            .then((resp) => {
                // ожидаем, что бэк теперь шлёт по каждому элементу поля:
                // id (FilePlayList.id)
                // fileGlobalId (FileGlobal.id)
                // title, url, position, playTime
                // rulesMode, rules[]
                const sortedFiles = (resp.data || [])
                    .slice()
                    .sort(
                        (a, b) =>
                            (a.position ?? 0) - (b.position ?? 0)
                    );
                setCurrentPlaylist(sortedFiles);
            })
            .finally(() => setLoading(false));
    }

    function getGallery() {
        api
            .get("/getFiles")
            .then((resp) => setMediaList(resp.data || []))
            .catch(() => setMediaList([]));
    }

    function fetchCompanies() {
        api.get("/admin/playlists/companies-with-devices").then((resp) => {
            const list = resp.data || [];
            setCompanies(list);

            // уже подвязанные к этому плейлисту компании
            const preChecked = list
                .filter(
                    (c) =>
                        c.playlistActive === parseInt(playlistId)
                )
                .map((c) => c.id);
            setSelectedCompanyIds(preChecked);
        });
    }

    // назначение всего плейлиста компании
    function handleCompanyToggle(companyId) {
        const willActivate = !selectedCompanyIds.includes(companyId);

        api.post("/admin/playlists/setCompanyPlaylist", {
            playlistId: parseInt(playlistId),
            companyId,
            active: willActivate,
        })
            .then(() => {
                setSelectedCompanyIds((prev) =>
                    willActivate
                        ? [...prev, companyId]
                        : prev.filter((id) => id !== companyId)
                );
            })
            .catch((e) => {
                alert(
                    `Ошибка назначения плейлиста компании: ${
                        e?.response?.data?.error || e.message || e
                    }`
                );
            });
    }

    const filteredCompanies = companies.filter(
        (c) =>
            c.devices &&
            c.devices.length > 0 &&
            ((c.name || "")
                    .toLowerCase()
                    .includes(search.toLowerCase()) ||
                (c.city || "")
                    .toLowerCase()
                    .includes(search.toLowerCase()) ||
                (c.address || "")
                    .toLowerCase()
                    .includes(search.toLowerCase()) ||
                (c.region || "")
                    .toLowerCase()
                    .includes(search.toLowerCase()))
    );

    // =====================
    // Добавление нового файла в плейлист
    // =====================
    async function addNewPlaylistFile() {
        if (!selectedMedia) return;
        setAdding(true);
        try {
            const payload = {
                file_id: selectedMedia.id,
            };

            if (playTime !== "") {
                payload.playTime = Number(playTime);
            }

            if (insertMode === "interleave") {
                payload.mode = "interleave";
                payload.interval = Math.max(1, Number(intervalN) || 1);
            } else if (insertMode === "once") {
                payload.mode = "once";
                if (oncePosition !== "") {
                    payload.position = Math.max(
                        1,
                        Number(oncePosition) || 1
                    );
                }
            } else if (insertMode === "afterType") {
                payload.mode = "afterType";
                payload.targetType = targetType; // "main" | "promo" | "ad"
            } else if (insertMode === "beforeType") {
                payload.mode = "beforeType";
                payload.targetType = targetType;
            }

            await api.post(`/admin/playlists/${playlistId}/files`, payload);

            // сброс формы
            setAddFormOpen(false);
            setSelectedMedia(null);
            setPlayTime("");
            setOncePosition("");
            setIntervalN(1);
            setInsertMode("once");
            setTargetType("main");

            getPlayListById();
        } catch (e) {
            alert(
                `Ошибка добавления: ${
                    e?.response?.data?.error || e.message || e
                }`
            );
        } finally {
            setAdding(false);
        }
    }

    // =====================
    // Drag & Drop reorder
    // =====================
    function onDragEnd(result) {
        if (!result.destination) return;
        const reordered = Array.from(visiblePlaylist);
        const [removed] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, removed);

        let visibleIndex = 0;
        const merged = currentPlaylist.map((item) =>
            hiddenFileIds.has(Number(item.fileGlobalId))
                ? item
                : reordered[visibleIndex++]
        );
        const withPositions = merged.map((item, i) => ({
            ...item,
            position: i + 1,
        }));
        setCurrentPlaylist(withPositions);
    }

    function handleSaveOrder() {
        setSavingOrder(true);
        api.post("/admin/playlists/savePlaylistOrder", {
            idPlayList: playlistId,
            playList: currentPlaylist.map((item) => ({
                idFile: item.id,
                position: item.position,
            })),
        })
            .then(getPlayListById)
            .finally(() => setSavingOrder(false));
    }

    // =====================
    // Удаление файла из плейлиста
    // =====================
    function handleDeleteItem(el) {
        api.delete(`/admin/playlists/${playlistId}/files/${el.id}`)
            .then(getPlayListById)
            .catch((e) =>
                alert(
                    `Ошибка удаления: ${
                        e?.response?.data?.error || e.message || e
                    }`
                )
            );
    }

    // =====================
    // Таргетинг (whitelist / blacklist)
    // =====================

    // открыть модалку редактирования таргетинга для конкретного item
    function openRuleEditor(item) {
        setRuleEditItemId(item.id);
        setRuleEditFileGlobalId(item.fileGlobalId ?? null);

        // начальные данные из item.rulesMode / item.rules
        const initialMode = item.rulesMode || null;
        setRuleMode(initialMode);

        const initialCompanies = Array.isArray(item.rules)
            ? item.rules
                .map((r) => r.companyId)
                .filter(Boolean)
            : [];
        setRuleSelectedCompanyIds(Array.from(new Set(initialCompanies)));

        setRuleSearch("");
        setRuleApplyAllSame(false);
    }

    function closeRuleEditor() {
        setRuleEditItemId(null);
        setRuleEditFileGlobalId(null);
        setRuleMode(null);
        setRuleSelectedCompanyIds([]);
        setRuleSearch("");
        setRuleApplyAllSame(false);
    }

    function toggleRuleCompany(companyId) {
        setRuleSelectedCompanyIds((prev) =>
            prev.includes(companyId)
                ? prev.filter((id) => id !== companyId)
                : [...prev, companyId]
        );
    }

    const filteredRuleCompanies = companies.filter((c) => {
        const term = ruleSearch.trim().toLowerCase();
        if (!term) return true;
        return (
            (c.name || "").toLowerCase().includes(term) ||
            (c.city || "").toLowerCase().includes(term) ||
            (c.address || "").toLowerCase().includes(term) ||
            (c.region || "").toLowerCase().includes(term)
        );
    });

    async function saveRule() {
        if (!ruleEditItemId) return;

        // если mode не выбран или список пустой -> шлём null (снять таргетинг)
        const payload =
            !ruleMode ||
            !ruleSelectedCompanyIds ||
            ruleSelectedCompanyIds.length === 0
                ? {
                    mode: null,
                    companyIds: [],
                    sameFile: false,
                }
                : {
                    mode: ruleMode, // "whitelist"|"blacklist"
                    companyIds: ruleSelectedCompanyIds,
                    sameFile: !!ruleApplyAllSame,
                    fileGlobalId: ruleEditFileGlobalId,
                    // fileGlobalId нужен бэку, чтобы он нашёл "одинаковые" файлы
                };

        try {
            await api.post(
                `/admin/playlists/file-rule/${ruleEditItemId}`,
                payload
            );
            closeRuleEditor();
            getPlayListById(); // перезагрузим список роликов и их правила
        } catch (e) {
            alert(
                `Ошибка сохранения таргетинга: ${
                    e?.response?.data?.error || e.message || e
                }`
            );
        }
    }

    // =====================
    // Сохранение заголовка плейлиста (пока TODO)
    // =====================
    function handleSaveTitle() {
        // api.put(`/admin/playlists/${playlistId}`, { title: titlePlaylist })
        //   .catch(...)
    }

    return (
        <div className="detail-playlist">
            <div className="detail-header">
                <input
                    className="detail-title"
                    value={titlePlaylist}
                    onChange={(e) => setTitlePlaylist(e.target.value)}
                    onBlur={handleSaveTitle}
                    maxLength={80}
                    placeholder="Название плейлиста"
                />
                <div className="detail-actions">
                    <button
                        className="btn primary"
                        onClick={handleSaveOrder}
                        disabled={savingOrder}
                    >
                        {savingOrder ? "Сохраняем..." : "Сохранить порядок"}
                    </button>
                    <button className="btn danger">
                        <span role="img" aria-label="del">
                            🗑
                        </span>{" "}
                        Удалить
                    </button>
                </div>
            </div>

            <div className="detail-content">
                <div className="detail-main">
                    <h4>Медиафайлы в плейлисте</h4>
                    {loading ? (
                        <div className="detail-loading">Загрузка...</div>
                    ) : (
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="playlist-droppable">
                                {(provided) => (
                                    <div
                                        className="playlist-sequence"
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                    >
                                        {visiblePlaylist.length === 0 && (
                                            <div className="playlist-empty">
                                                Нет файлов в плейлисте
                                            </div>
                                        )}

                                        {visiblePlaylist.map((el, i) => (
                                            <Draggable
                                                key={el.id}
                                                draggableId={String(el.id)}
                                                index={i}
                                            >
                                                {(draggableProvided, snapshot) => (
                                                    <div
                                                        className={
                                                            "playlist-seq-card" +
                                                            (snapshot.isDragging
                                                                ? " dragging"
                                                                : "")
                                                        }
                                                        ref={draggableProvided.innerRef}
                                                        {...draggableProvided.draggableProps}
                                                        {...draggableProvided.dragHandleProps}
                                                    >
                                                        <div className="seq-preview">
                                                            <div
                                                                className={
                                                                    "media-pill media-pill--" +
                                                                    inferMediaKind(
                                                                        el
                                                                    )
                                                                }
                                                            >
                                                                <span className="media-pill__label">
                                                                    {getMediaKindLabel(
                                                                        inferMediaKind(
                                                                            el
                                                                        )
                                                                    )}
                                                                </span>
                                                                {getPlaylistCategoryLabel(
                                                                    el.fileType
                                                                ) && (
                                                                    <span className="media-pill__meta">
                                                                        {getPlaylistCategoryLabel(
                                                                            el.fileType
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="seq-info">
                                                            <div className="seq-title">
                                                                {el.title}
                                                            </div>
                                                            <div className="seq-meta">
                                                                <span>
                                                                    Тип:{" "}
                                                                    {getMediaKindLabel(
                                                                        inferMediaKind(
                                                                            el
                                                                        )
                                                                    )}
                                                                </span>
                                                                {getPlaylistCategoryLabel(
                                                                    el.fileType
                                                                ) && (
                                                                    <span>
                                                                        Категория:{" "}
                                                                        {getPlaylistCategoryLabel(
                                                                            el.fileType
                                                                        )}
                                                                    </span>
                                                                )}
                                                                <span>
                                                                    Позиция:{" "}
                                                                    {el.position ?? i + 1}
                                                                </span>
                                                            </div>

                                                            {/* подсветка текущего таргетинга */}
                                                            {el.rulesMode && (
                                                                <div className="seq-rules">
                                                                    {el.rulesMode ===
                                                                    "whitelist"
                                                                        ? "Показывать только в:"
                                                                        : el.rulesMode ===
                                                                        "blacklist"
                                                                            ? "Не показывать в:"
                                                                            : null}

                                                                    {el.rules &&
                                                                    el.rules
                                                                        .slice(
                                                                            0,
                                                                            3
                                                                        )
                                                                        .map(
                                                                            (
                                                                                r,
                                                                                idx
                                                                            ) => (
                                                                                <span
                                                                                    key={
                                                                                        idx
                                                                                    }
                                                                                    className="seq-rule-chip"
                                                                                >
                                                                                        {r.city ||
                                                                                        r.companyName ||
                                                                                        r.address ||
                                                                                        `ID:${r.companyId}`}
                                                                                    </span>
                                                                            )
                                                                        )}

                                                                    {el.rules &&
                                                                    el.rules
                                                                        .length >
                                                                    3 && (
                                                                        <span className="seq-rule-chip more">
                                                                                +
                                                                            {el.rules.length -
                                                                            3}
                                                                            </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="seq-controls">
                                                            <button
                                                                className="seq-delete"
                                                                onClick={() =>
                                                                    handleDeleteItem(
                                                                        el
                                                                    )
                                                                }
                                                                title="Удалить из плейлиста"
                                                            >
                                                                🗑
                                                            </button>

                                                            <button
                                                                className="seq-target"
                                                                onClick={() =>
                                                                    openRuleEditor(
                                                                        el
                                                                    )
                                                                }
                                                                title="Таргетинг показа"
                                                            >
                                                                🎯
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}

                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}

                    {!addFormOpen && (
                        <button
                            className="add-media-btn"
                            onClick={() => setAddFormOpen(true)}
                        >
                            <span>＋</span> Добавить медиа
                        </button>
                    )}

                    {addFormOpen && (
                        <div className="add-media-form">
                            <h5>Выберите из галереи</h5>

                            <div className="add-media-gallery">
                                {mediaList.map((m) => (
                                    <div
                                        key={m.id}
                                        className={
                                            "add-media-tile" +
                                            (selectedMedia &&
                                            m.id === selectedMedia.id
                                                ? " selected"
                                                : "")
                                        }
                                        onClick={() => setSelectedMedia(m)}
                                    >
                                        <div
                                            className={
                                                "add-media-thumb add-media-thumb--" +
                                                inferMediaKind(m)
                                            }
                                        >
                                            <span className="add-media-thumb__label">
                                                {getMediaKindLabel(
                                                    inferMediaKind(m)
                                                )}
                                            </span>
                                            <span className="add-media-thumb__meta">
                                                {m.type || "Без типа"}
                                            </span>
                                        </div>

                                        <span className="add-media-title">
                                            {m.title || "Без названия"}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="add-media-options">
                                <div className="row">
                                    <label>
                                        Время показа, сек (опц.)
                                        <input
                                            type="number"
                                            min="0"
                                            value={playTime}
                                            onChange={(e) =>
                                                setPlayTime(e.target.value)
                                            }
                                            placeholder="например, 15"
                                        />
                                    </label>
                                </div>

                                <fieldset className="row">
                                    <legend>Режим вставки</legend>

                                    {/* once */}
                                    <label className="radio">
                                        <input
                                            type="radio"
                                            name="insert-mode"
                                            value="once"
                                            checked={insertMode === "once"}
                                            onChange={() =>
                                                setInsertMode("once")
                                            }
                                        />
                                        <span>Один раз</span>
                                    </label>

                                    {insertMode === "once" && (
                                        <div className="subrow">
                                            <label>
                                                Позиция (опц.)
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={oncePosition}
                                                    onChange={(e) =>
                                                        setOncePosition(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="пусто = в конец"
                                                />
                                            </label>
                                        </div>
                                    )}

                                    {/* interleave */}
                                    <label
                                        className="radio"
                                        style={{ marginTop: 8 }}
                                    >
                                        <input
                                            type="radio"
                                            name="insert-mode"
                                            value="interleave"
                                            checked={
                                                insertMode === "interleave"
                                            }
                                            onChange={() =>
                                                setInsertMode("interleave")
                                            }
                                        />
                                        <span>
                                            Через N позиций (равномерно)
                                        </span>
                                    </label>

                                    {insertMode === "interleave" && (
                                        <div className="subrow">
                                            <label>
                                                N
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={intervalN}
                                                    onChange={(e) =>
                                                        setIntervalN(
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </label>
                                            <div className="hint">
                                                Добавит ролик после каждого
                                                N-го элемента плейлиста.
                                            </div>
                                        </div>
                                    )}

                                    {/* afterType */}
                                    <label
                                        className="radio"
                                        style={{ marginTop: 8 }}
                                    >
                                        <input
                                            type="radio"
                                            name="insert-mode"
                                            value="afterType"
                                            checked={
                                                insertMode === "afterType"
                                            }
                                            onChange={() =>
                                                setInsertMode("afterType")
                                            }
                                        />
                                        <span>
                                            После каждого ролика типа…
                                        </span>
                                    </label>

                                    {insertMode === "afterType" && (
                                        <div className="subrow">
                                            <label>
                                                Тип якоря
                                                <select
                                                    value={targetType}
                                                    onChange={(e) =>
                                                        setTargetType(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option value="main">
                                                        Основной (main)
                                                    </option>
                                                    <option value="promo">
                                                        Промо (promo)
                                                    </option>
                                                    <option value="ad">
                                                        Реклама (ad)
                                                    </option>
                                                </select>
                                            </label>
                                            <div className="hint">
                                                Найдём каждый ролик этого
                                                типа и вставим выбранный
                                                файл сразу ПОСЛЕ него.
                                            </div>
                                        </div>
                                    )}

                                    {/* beforeType */}
                                    <label
                                        className="radio"
                                        style={{ marginTop: 8 }}
                                    >
                                        <input
                                            type="radio"
                                            name="insert-mode"
                                            value="beforeType"
                                            checked={
                                                insertMode === "beforeType"
                                            }
                                            onChange={() =>
                                                setInsertMode("beforeType")
                                            }
                                        />
                                        <span>
                                            Перед каждым роликом типа…
                                        </span>
                                    </label>

                                    {insertMode === "beforeType" && (
                                        <div className="subrow">
                                            <label>
                                                Тип якоря
                                                <select
                                                    value={targetType}
                                                    onChange={(e) =>
                                                        setTargetType(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option value="main">
                                                        Основной (main)
                                                    </option>
                                                    <option value="promo">
                                                        Промо (promo)
                                                    </option>
                                                    <option value="ad">
                                                        Реклама (ad)
                                                    </option>
                                                </select>
                                            </label>
                                            <div className="hint">
                                                Найдём каждый ролик этого
                                                типа и вставим выбранный
                                                файл ПЕРЕД ним.
                                            </div>
                                        </div>
                                    )}
                                </fieldset>
                            </div>

                            <div className="add-media-actions">
                                <button
                                    className="btn"
                                    onClick={() => setAddFormOpen(false)}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="btn primary"
                                    disabled={!selectedMedia || adding}
                                    onClick={addNewPlaylistFile}
                                >
                                    {adding ? "Добавление..." : "Добавить"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="detail-side">
                    <h4>Компании с устройствами</h4>
                    <input
                        type="text"
                        className="company-search"
                        placeholder="Поиск: город, адрес, регион..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                            marginBottom: 12,
                            width: "100%",
                            padding: "8px 10px",
                            borderRadius: 8,
                            border: "1px solid #ddd",
                        }}
                    />
                    <div className="company-list">
                        {filteredCompanies.length === 0 && (
                            <div
                                style={{
                                    color: "#b0b7c3",
                                    padding: 12,
                                }}
                            >
                                Нет компаний
                            </div>
                        )}

                        {filteredCompanies.map((c) => (
                            <div className="company-row" key={c.id}>
                                <label
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        width: "100%",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedCompanyIds.includes(
                                            c.id
                                        )}
                                        onChange={() =>
                                            handleCompanyToggle(c.id)
                                        }
                                    />
                                    <div>
                                        <div
                                            style={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            {c.name || `Компания #${c.id}`}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: ".97em",
                                                color: "#3b4255",
                                            }}
                                        >
                                            {c.city}, {c.address}
                                            {c.region && (
                                                <span
                                                    style={{
                                                        color: "#8291b2",
                                                    }}
                                                >
                                                    {" "}
                                                    — {c.region}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Модалка таргетинга ролика */}
            {ruleEditItemId && (
                <div className="modal-overlay" onClick={closeRuleEditor}>
                    <div
                        className="modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-header">
                            <div className="modal-title">Таргетинг показа</div>
                            <button
                                className="modal-close"
                                onClick={closeRuleEditor}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal-subtitle">
                            Выберите режим и компании
                        </div>

                        <fieldset
                            className="row"
                            style={{ marginBottom: 12 }}
                        >
                            <legend>Где показывать</legend>

                            <label className="radio">
                                <input
                                    type="radio"
                                    name="rule-mode"
                                    value="all"
                                    checked={ruleMode === null || ruleMode === undefined}
                                    onChange={() => setRuleMode(null)}
                                />
                                <span>Везде (без ограничений)</span>
                            </label>

                            <label
                                className="radio"
                                style={{ marginTop: 8 }}
                            >
                                <input
                                    type="radio"
                                    name="rule-mode"
                                    value="whitelist"
                                    checked={ruleMode === "whitelist"}
                                    onChange={() => setRuleMode("whitelist")}
                                />
                                <span>
                                    Показывать ТОЛЬКО в выбранных компаниях
                                </span>
                            </label>

                            <label
                                className="radio"
                                style={{ marginTop: 8 }}
                            >
                                <input
                                    type="radio"
                                    name="rule-mode"
                                    value="blacklist"
                                    checked={ruleMode === "blacklist"}
                                    onChange={() => setRuleMode("blacklist")}
                                />
                                <span>
                                    НЕ показывать в выбранных компаниях
                                </span>
                            </label>
                        </fieldset>

                        {(ruleMode === "whitelist" ||
                            ruleMode === "blacklist") && (
                            <>
                                <div
                                    style={{
                                        fontSize: ".9em",
                                        color: "#666",
                                        marginBottom: 6,
                                    }}
                                >
                                    Фильтр компаний
                                </div>
                                <input
                                    type="text"
                                    className="company-search"
                                    placeholder="Найти компанию..."
                                    value={ruleSearch}
                                    onChange={(e) =>
                                        setRuleSearch(e.target.value)
                                    }
                                    style={{
                                        marginBottom: 12,
                                        width: "100%",
                                        padding: "8px 10px",
                                        borderRadius: 8,
                                        border: "1px solid #ddd",
                                    }}
                                />

                                <div
                                    className="company-list"
                                    style={{
                                        maxHeight: 250,
                                        overflowY: "auto",
                                        border: "1px solid #eee",
                                        borderRadius: 8,
                                        padding: 8,
                                        marginBottom: 12,
                                    }}
                                >
                                    {filteredRuleCompanies.length === 0 && (
                                        <div
                                            style={{
                                                color: "#b0b7c3",
                                                padding: 12,
                                                fontSize: ".9em",
                                            }}
                                        >
                                            Нет совпадений
                                        </div>
                                    )}

                                    {filteredRuleCompanies.map((c) => (
                                        <div
                                            className="company-row"
                                            key={c.id}
                                            style={{
                                                borderBottom:
                                                    "1px solid #eef3fc",
                                                paddingBottom: 6,
                                                marginBottom: 6,
                                            }}
                                        >
                                            <label
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 8,
                                                    width: "100%",
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={ruleSelectedCompanyIds.includes(
                                                        c.id
                                                    )}
                                                    onChange={() =>
                                                        toggleRuleCompany(
                                                            c.id
                                                        )
                                                    }
                                                />
                                                <div>
                                                    <div
                                                        style={{
                                                            fontWeight: 600,
                                                            fontSize: ".95em",
                                                        }}
                                                    >
                                                        {c.name ||
                                                        `Компания #${c.id}`}
                                                    </div>
                                                    <div
                                                        style={{
                                                            fontSize: ".85em",
                                                            color: "#3b4255",
                                                        }}
                                                    >
                                                        {c.city}, {c.address}
                                                        {c.region && (
                                                            <span
                                                                style={{
                                                                    color: "#8291b2",
                                                                }}
                                                            >
                                                                {" "}
                                                                — {c.region}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </label>
                                        </div>
                                    ))}
                                </div>

                                {/* Новый чекбокс: применить эти правила ко всем одинаковым файлам */}
                                <label
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        marginBottom: 16,
                                        fontSize: ".9em",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={ruleApplyAllSame}
                                        onChange={() =>
                                            setRuleApplyAllSame(
                                                (v) => !v
                                            )
                                        }
                                        disabled={
                                            !ruleEditFileGlobalId
                                        }
                                    />
                                    <span>
                                        Применить к повторам этого
                                        же файла (одинаковый ролик,
                                        добавленный несколько раз
                                        в плейлисте)
                                    </span>
                                </label>
                            </>
                        )}

                        <div className="modal-footer">
                            <button
                                className="modern-upload-cancel"
                                onClick={closeRuleEditor}
                            >
                                Отмена
                            </button>
                            <button
                                className="modern-upload-btn"
                                onClick={saveRule}
                            >
                                Сохранить
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
